import React, { useEffect, useState } from "react";
import { GripVertical, Eye, EyeOff, Star, ChevronUp, ChevronDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { AuthImage } from "./FileUpload";

function Row({ m, i, n, onMove, onHide, onFeature, dragProps }) {
  const off = m.hidden || m.self_hidden || m.directory_hidden;
  return (
    <li className={`flex items-center gap-3 rounded-xl border p-3 text-sm ${off ? "border-stone-300 bg-stone-100 opacity-70" : "border-gold/30 bg-parchment-card"}`} data-testid={`spotlight-row-${m.id}`} {...dragProps}>
      <span className="hidden cursor-grab text-muted-foreground sm:block" title="Drag to reorder"><GripVertical size={18} /></span>
      <div className="flex flex-col sm:hidden">
        <button onClick={() => onMove(i, i - 1)} disabled={i === 0} className="h-7 w-7 disabled:opacity-30" aria-label="Move up" data-testid={`spotlight-up-${m.id}`}><ChevronUp size={16} /></button>
        <button onClick={() => onMove(i, i + 1)} disabled={i === n - 1} className="h-7 w-7 disabled:opacity-30" aria-label="Move down" data-testid={`spotlight-down-${m.id}`}><ChevronDown size={16} /></button>
      </div>
      {m.photo_url ? <AuthImage src={m.photo_url} alt="" className="h-10 w-10 rounded-full object-cover" /> : <span className="flex h-10 w-10 items-center justify-center rounded-full bg-crimson font-bold text-parchment">{m.first_name[0]}</span>}
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">{m.name} {m.featured && <Star size={12} className="inline fill-gold text-gold" />}</p>
        <p className="text-xs text-muted-foreground">{m.hometown || "—"} · {m.joined || "no approval date"}{m.self_hidden && " · member opted out"}{m.directory_hidden && " · not in directory"}</p>
      </div>
      <Button size="sm" variant={m.featured ? "default" : "outline"} className={`h-9 ${m.featured ? "bg-gold text-crimson hover:bg-gold/90" : ""}`} onClick={() => onFeature(m)} title="Keep on the homepage beyond 3 months" data-testid={`spotlight-feature-${m.id}`}><Star size={14} /></Button>
      <Button size="sm" variant="outline" className="h-9" onClick={() => onHide(m)} title={m.hidden ? "Show on homepage" : "Hide from homepage"} data-testid={`spotlight-hide-${m.id}`}>{m.hidden ? <Eye size={14} /> : <EyeOff size={14} />}</Button>
    </li>
  );
}

export function SpotlightManager() {
  const { data, reload } = useApi("/admin/spotlight");
  const [items, setItems] = useState([]);
  const [drag, setDrag] = useState(null);
  useEffect(() => { if (data) setItems(data.items); }, [data]);
  const saveOrder = async (list) => {
    setItems(list);
    try { await api.put("/admin/spotlight/order", { ids: list.map((m) => m.id) }); } catch (e) { toast.error(formatError(e.response?.data?.detail)); reload(); }
  };
  const move = (from, to) => { if (to < 0 || to >= items.length) return; const l = [...items]; const [x] = l.splice(from, 1); l.splice(to, 0, x); saveOrder(l); };
  const hide = async (m) => {
    try { await api.put(`/admin/spotlight/${m.id}/hide`, { hidden: !m.hidden }); toast.success(m.hidden ? `${m.first_name} is back on the homepage` : `${m.first_name} hidden from the homepage`); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const feature = async (m) => {
    try { await api.put(`/admin/members/${m.id}`, { spotlight_featured: !m.featured }); toast.success(m.featured ? "No longer pinned" : `${m.first_name} pinned to the homepage`); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const dragProps = (i) => ({
    draggable: true,
    onDragStart: () => setDrag(i),
    onDragOver: (e) => e.preventDefault(),
    onDrop: () => { if (drag !== null && drag !== i) move(drag, i); setDrag(null); },
  });
  const live = items.filter((m) => !m.hidden && !m.self_hidden && !m.directory_hidden).length;
  return (
    <div className="glass rounded-2xl p-6" data-testid="spotlight-manager">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-serif-display text-xl font-bold text-crimson">Homepage "Welcome our new members"</h3>
          <p className="text-xs text-muted-foreground">Members approved in the last {data?.days || 90} days appear automatically; ★ pins someone longer. Drag (or use the arrows on a phone) to set the order shown to visitors. <b>{live}</b> currently visible.</p>
        </div>
        <a href="/#new-members" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-palm underline-offset-4 hover:underline" data-testid="spotlight-view-site"><ExternalLink size={14} /> View on site</a>
      </div>
      {items.length === 0 ? <p className="rounded-xl border border-dashed border-gold/40 p-6 text-center text-sm text-muted-foreground" data-testid="spotlight-empty">Nobody is in the strip right now. Approve an application, or open a member under Members → Manage and turn on "Feature in Welcome our new members".</p>
        : <ul className="space-y-2">{items.map((m, i) => <Row key={m.id} m={m} i={i} n={items.length} onMove={move} onHide={hide} onFeature={feature} dragProps={dragProps(i)} />)}</ul>}
    </div>
  );
}
